"use client";

import Link from "next/link";
import {useEffect, useId, useRef, useState, type ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {parseToolResult} from "@repo/ui/gailgpt/code-execution";
import type {ApprovalVisualState} from "@repo/ui/gailgpt/approval-status-banner";
import Icon from "@/components/library/Icon";
import {workflowBuilderPath} from "@/utils/common/constants/routes";
import {useWorkflowPublication} from "@/contexts/chat/WorkflowPublicationContext";
import type {MessageContentFunctionCall} from "@/services/wingman";
import {readPublicationReceipt} from "./workflowPublicationReceipt";
import {workflowPublicationRuns} from "./workflowPublicationRuns";
import type {useToolApprovalDecision} from "./useToolApprovalDecision";
import WorkflowCard, {WorkflowCardFooter} from "./WorkflowCard";
import {workflowPublicationSummary, type PublicationWorkflow} from "./workflowPublicationSummary";

type Decision = ReturnType<typeof useToolApprovalDecision>;

/** The approval receipt owns publication state; accepting a request is not proof of publication. */
export default function WorkflowPublishApproval({workflow, call, decision, result, notices}: {
   workflow: PublicationWorkflow;
   call: MessageContentFunctionCall;
   decision: Decision;
   result?: unknown;
   notices: ReactNode;
}) {
   const {t, i18n} = useTranslation("ask");
   const {t: wt} = useTranslation("workflows");
   const {deferred, restored, defer} = useDeferredPublication(call.approval!.id);
   const context = useWorkflowPublication();
   const output = parseToolResult(result);
   const publishedWorkflow = publicationReceipt(workflow, decision, output);
   const published = publishedWorkflow !== undefined;
   const {state, bannerState, visualState, unknownOutcome} = publicationPresentation(decision, published, deferred);
   const reviewedWorkflow = publishedWorkflow ?? workflow;
   const {id, name} = reviewedWorkflow;
   const summary = workflowPublicationSummary(workflow, t, wt, i18n.language);
   const registrationFailures = published ? ["webhooks", "tool", "toolMetadata"].filter(key => parseToolResult(output?.[key])?.status === "failed") : [];
   const editor = id ? <Link className="approval-open inline-flex items-center gap-1.5" href={workflowBuilderPath(id)} onNavigate={context?.onOpenInEditor}>
      {t("workflowPanel.openInEditor")}<Icon icon="arrow-up-right-from-square" className="h-3 w-3" />
   </Link> : null;
   const review = useFreshPublicationReview(name, id);

   return <WorkflowCard workflow={reviewedWorkflow} summary={summary}
      title={published ? name : t("publishWorkflow.title", {name})}
      status={t(`publishWorkflow.banner.${bannerState}`)}
      configuration={call.parameters} state={visualState} publishState={state} label={t("publishWorkflow.cardLabel", {name})} showSteps={state === "pending" || published}>
      {published && id && <WorkflowPublicationTest workflowId={id} callId={call.callId} editor={editor} />}
      {state !== "publishing" && <WorkflowCardFooter compact={published || state === "declined"}>
         {state === "pending" && <PublicationReview decision={decision} restored={restored} defer={defer} review={review}
            consequence={summary.consequence} notices={notices} unknownOutcome={unknownOutcome} editor={editor} />}
         {state === "declined" && <DeclinedPublication decision={decision} defer={defer} review={review} />}
         {published && <>
            {registrationFailures.map(key => <p role="alert" key={key}>{t(`publishWorkflow.registrationFailed.${key}`)}</p>)}
            <div className="flex flex-wrap items-center gap-2"><p className="flex-1">{t("publishWorkflow.approved")}</p>{editor}</div>
         </>}
      </WorkflowCardFooter>}
   </WorkflowCard>;
}

function useDeferredPublication(approvalId: string) {
   const storageKey = `gail-workflow-deferred:${approvalId}`;
   const [deferred, setDeferred] = useState(false);
   const [restored, setRestored] = useState(false);
   useEffect(() => {
      try {setDeferred(localStorage.getItem(storageKey) === "true");} catch { /* Private browsing may disable storage. */ }
      setRestored(true);
   }, [storageKey]);
   const defer = (value: boolean) => {
      setDeferred(value);
      try {
         if (value) localStorage.setItem(storageKey, "true");
         else localStorage.removeItem(storageKey);
      } catch { /* Keep the current session usable if storage is unavailable. */ }
   };
   return {deferred, restored, defer};
}

/** A matching published receipt is the only source of published identity. */
function publicationReceipt(workflow: PublicationWorkflow, decision: Decision, output: ReturnType<typeof parseToolResult>): PublicationWorkflow | undefined {
   const receipt = readPublicationReceipt(output, workflow.id);
   if (decision.status !== "completed" || !receipt) return undefined;
   return {...workflow, id: receipt.id,
      name: typeof receipt.name === "string" ? receipt.name : workflow.name,
      revision: typeof receipt.revision === "number" ? receipt.revision : workflow.revision};
}

type PublishState = "published" | "publishing" | "declined" | "pending";

function publicationState(published: boolean, publishing: boolean, declined: boolean): PublishState {
   if (published) return "published";
   if (publishing) return "publishing";
   if (declined) return "declined";
   return "pending";
}

function publicationPresentation(decision: Decision, published: boolean, deferred: boolean) {
   const publishing = decision.status === "approved" || (decision.status === "submitting" && decision.pending !== "discard");
   const declined = decision.status === "denied" || (!decision.settled && deferred);
   const state = publicationState(published, publishing, declined);
   const unknownOutcome = decision.status === "completed" && !published;
   let bannerState: PublishState | "unknown" | "failed" = state;
   if (decision.settled && !published && !publishing && !declined) bannerState = "failed";
   if (unknownOutcome || decision.status === "indeterminate") bannerState = "unknown";
   const visualStates: Record<typeof bannerState, ApprovalVisualState> = {
      unknown: "indeterminate", failed: "failed", published: "completed", publishing: "approved", declined: "denied", pending: "required",
   };
   // The banner can report uncertainty while the controls remain pending.
   return {state, bannerState, visualState: visualStates[bannerState], unknownOutcome};
}

function useFreshPublicationReview(name: string, id: PublicationWorkflow["id"]) {
   const {t} = useTranslation("ask");
   const context = useWorkflowPublication();
   const [requesting, setRequesting] = useState(false);
   const [error, setError] = useState(false);
   const lock = useRef(false);
   const requestFresh = async () => {
      if (!context || context.disabled || lock.current) return;
      lock.current = true;
      setRequesting(true);
      setError(false);
      try {setError(!await context.onFollowUp(t("publishWorkflow.requestFresh", {name, id: id ?? name})));}
      catch {setError(true);}
      finally {lock.current = false; setRequesting(false);}
   };
   return {available: !!context, disabled: context?.disabled || requesting, error, requestFresh};
}

type FreshPublicationReview = ReturnType<typeof useFreshPublicationReview>;

function PublicationReview({decision, restored, defer, review, consequence, notices, unknownOutcome, editor}: {
   decision: Decision;
   restored: boolean;
   defer: (value: boolean) => void;
   review: FreshPublicationReview;
   consequence: string;
   notices: ReactNode;
   unknownOutcome: boolean;
   editor: ReactNode;
}) {
   const {t} = useTranslation("ask");
   const descriptionId = useId();
   return <>
      <p id={descriptionId}>{consequence}</p>
      {notices}
      {unknownOutcome && <p role="alert">{t("publishWorkflow.unknownOutcome")}</p>}
      {review.error && <p role="alert">{t("publishWorkflow.followUpFailed")}</p>}
      <div className="flex flex-wrap items-center gap-2">{editor}
         <div className="ml-auto flex flex-wrap justify-end gap-2">
            {!decision.settled && decision.status !== "unavailable" ? <>
               <button type="button" className="approval-reject" disabled={!decision.canRespond || !restored || decision.queued != null} onClick={() => defer(true)}>{t("publishWorkflow.notNow")}</button>
               <button type="button" className="approval-approve" aria-describedby={descriptionId} disabled={!decision.canApply || !restored}
                  onKeyDown={event => {if (event.repeat && (event.key === "Enter" || event.key === " ")) event.preventDefault();}}
                  onClick={event => {if (event.detail <= 1) void decision.respond(true);}}>{t("publishWorkflow.publish")}</button>
            </> : review.available && <button type="button" className="approval-reject" disabled={review.disabled}
               onClick={() => void review.requestFresh()}>{t("publishWorkflow.reviewAgain")}</button>}
         </div>
      </div>
   </>;
}

function DeclinedPublication({decision, defer, review}: {decision: Decision; defer: (value: boolean) => void; review: FreshPublicationReview}) {
   const {t} = useTranslation("ask");
   const canUndo = !decision.settled && decision.status !== "unavailable";
   return <>
      <div className="flex flex-wrap items-center gap-2"><p className="flex-1">{t("publishWorkflow.declined")}</p>
         {canUndo && <button type="button" className="approval-open" disabled={!decision.canRespond} onClick={() => defer(false)}>{t("publishWorkflow.undo")}</button>}
         {!canUndo && review.available && <button type="button" className="approval-open" disabled={review.disabled} onClick={() => void review.requestFresh()}>{t("publishWorkflow.reviewAgain")}</button>}
      </div>
      {review.error && <p role="alert">{t("publishWorkflow.followUpFailed")}</p>}
   </>;
}

function WorkflowPublicationTest({workflowId, callId, editor}: {workflowId: string; callId: string; editor: ReactNode}) {
   const {t} = useTranslation("ask");
   const context = useWorkflowPublication();
   const [requesting, setRequesting] = useState(false);
   const [error, setError] = useState(false);
   const [request, setRequest] = useState<{previousRunId?: string}>();
   const lock = useRef(false);
   const {runs: laterRuns, superseded} = workflowPublicationRuns(context?.messages ?? [], callId, workflowId);
   const latestRun = laterRuns.at(-1);
   // A request waits for a new run, or resumes a paused session in this publication's history.
   const run = request && latestRun?.id === request.previousRunId ? undefined : latestRun;
   const {failed, passed, detail, showTest} = publicationTestPresentation(run, requesting, error, !!request);
   const test = async () => {
      if (!context || context.disabled || superseded || lock.current) return;
      lock.current = true;
      setRequest({previousRunId: latestRun?.status === "paused" ? undefined : latestRun?.id});
      setRequesting(true);
      setError(false);
      try {setError(!await context.onTest(workflowId));} catch {setError(true);}
      finally {lock.current = false; setRequesting(false);}
   };
   return <section className="mx-4 mb-3.5 flex flex-wrap items-center gap-3 rounded-lg border border-[var(--approval-border)] bg-[var(--approval-subtle)] p-3" aria-label={t("workflowPanel.test")}>
      <div className="min-w-0 flex-1 basis-[200px]" aria-live="polite">
         <h4 className="text-[13px] font-medium leading-[19px]">{t(passed || failed ? "publishWorkflow.testComplete" : superseded ? "workflowPanel.test" : "publishWorkflow.testNext")}</h4>
         <p className={`text-xs leading-[18px] ${failed || error ? "text-[var(--approval-error)]" : "text-[var(--approval-muted)]"}`} role={error ? "alert" : undefined}>{t(`publishWorkflow.${superseded && !run ? "testSuperseded" : detail}`)}</p>
         {failed && editor}
      </div>
      {showTest && <button type="button" className="approval-approve !px-3.5 !text-[13px]" disabled={!context || context.disabled || superseded} onClick={() => void test()}>{t("workflowPanel.test")}</button>}
   </section>;
}

type PublicationRun = ReturnType<typeof workflowPublicationRuns>["runs"][number];

function publicationTestPresentation(run: PublicationRun | undefined, requesting: boolean, error: boolean, requested: boolean) {
   const running = requesting || !!run && ["running", "stepping", "stopping"].includes(run.status);
   const failed = !!run && ["failed", "timed_out", "cancelled"].includes(run.status);
   const passed = run?.status === "completed";
   const showTest = !running && !passed && !failed && (!requested || error || !run);
   return {failed, passed, showTest, detail: publicationTestDetail(run, {error, running, passed, failed, requested})};
}

function publicationTestDetail(run: PublicationRun | undefined, state: {error: boolean; running: boolean; passed: boolean; failed: boolean; requested: boolean}) {
   if (state.error) return "testNotStarted";
   if (state.running) return "testRunning";
   if (state.passed) return "testPassed";
   if (state.failed) return "testFailed";
   if (run?.status === "paused") return "testPaused";
   if (state.requested) return "testWaiting";
   return "testHint";
}
